import { Link } from 'react-router-dom';

export default function GettingStartedPage() {
  return (
    <div className="min-h-screen bg-base-100">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-primary mb-6">Getting Started</h1>

        {/* Introduction */}
        <section className="mb-12">
          <p className="text-lg text-base-content mb-4">
            TBit External Apps allows you to embed powerful, dynamic applications directly into your
            website. Get started in minutes with our simple embed script.
          </p>
        </section>

        {/* Quick Start */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Quick Start</h2>

          <div className="card bg-base-200 shadow-md mb-6">
            <div className="card-body">
              <h3 className="text-xl font-semibold mb-3">Step 1: Include the Script</h3>
              <p className="text-base-content/80 mb-3">
                Add the TBit Forms embed script to your HTML page:
              </p>
              <div className="mockup-code">
                <pre data-prefix="$">
                  <code>{'<script src="https://exapps.tbit.io/embed/forms.js"></script>'}</code>
                </pre>
              </div>
            </div>
          </div>

          <div className="card bg-base-200 shadow-md mb-6">
            <div className="card-body">
              <h3 className="text-xl font-semibold mb-3">Step 2: Add the Container</h3>
              <p className="text-base-content/80 mb-3">
                Place a container element where you want the form to appear:
              </p>
              <div className="mockup-code">
                <pre data-prefix="$">
                  <code>{'<div id="tbit-form" data-root-id="YOUR_FORM_ID"></div>'}</code>
                </pre>
              </div>
            </div>
          </div>

          <div className="card bg-base-200 shadow-md">
            <div className="card-body">
              <h3 className="text-xl font-semibold mb-3">Step 3: Get Your Root ID</h3>
              <p className="text-base-content/80">
                Create a form in your TBit dashboard and copy the Root ID. Replace{' '}
                <code className="bg-base-300 px-2 py-1 rounded">YOUR_FORM_ID</code> with your actual
                root ID.
              </p>
            </div>
          </div>
        </section>

        {/* Configuration Options */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Configuration Options</h2>

          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Attribute</th>
                  <th>Description</th>
                  <th>Default</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code className="text-sm">data-root-id</code>
                  </td>
                  <td>Your root ID (required)</td>
                  <td>-</td>
                  <td>
                    <code className="text-xs">507f1f77bcf86cd799439011</code>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code className="text-sm">data-width</code>
                  </td>
                  <td>Form width</td>
                  <td>100%</td>
                  <td>
                    <code className="text-xs">600px</code>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code className="text-sm">data-height</code>
                  </td>
                  <td>Form height</td>
                  <td>auto</td>
                  <td>
                    <code className="text-xs">800px</code>
                  </td>
                </tr>
                <tr>
                  <td>
                    <code className="text-sm">data-theme</code>
                  </td>
                  <td>Color theme</td>
                  <td>tbitLight</td>
                  <td>
                    <code className="text-xs">tbitDark</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Advanced Usage */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Advanced Usage</h2>

          <div className="card bg-base-200 shadow-md mb-6">
            <div className="card-body">
              <h3 className="text-xl font-semibold mb-3">Custom Event Listeners</h3>
              <p className="text-base-content/80 mb-3">Listen for form submission events:</p>
              <div className="mockup-code">
                <pre data-prefix="1">
                  <code>
                    {
                      'document.getElementById("tbit-form").addEventListener("tbitFormSubmitted", (e) => {'
                    }
                  </code>
                </pre>
                <pre data-prefix="2">
                  <code>{'  console.log("Form submitted:", e.detail);'}</code>
                </pre>
                <pre data-prefix="3">
                  <code>{'  // e.detail.rootId - The root ID'}</code>
                </pre>
                <pre data-prefix="4">
                  <code>{'  // e.detail.submissionId - The submission ID'}</code>
                </pre>
                <pre data-prefix="5">
                  <code>{'});'}</code>
                </pre>
              </div>
            </div>
          </div>

          <div className="card bg-base-200 shadow-md mb-6">
            <div className="card-body">
              <h3 className="text-xl font-semibold mb-3">JavaScript API</h3>
              <p className="text-base-content/80 mb-3">
                Use the TBitForms API for programmatic control:
              </p>
              <div className="mockup-code">
                <pre data-prefix="1">
                  <code>{'// Initialize forms manually'}</code>
                </pre>
                <pre data-prefix="2">
                  <code>{'TBitForms.init();'}</code>
                </pre>
                <pre data-prefix="3">
                  <code>{''}</code>
                </pre>
                <pre data-prefix="4">
                  <code>{'// Reload a specific form'}</code>
                </pre>
                <pre data-prefix="5">
                  <code>{'TBitForms.reload("YOUR_ROOT_ID");'}</code>
                </pre>
                <pre data-prefix="6">
                  <code>{''}</code>
                </pre>
                <pre data-prefix="7">
                  <code>{'// Change theme for all forms'}</code>
                </pre>
                <pre data-prefix="8">
                  <code>{'TBitForms.setTheme("tbitDark");'}</code>
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Examples */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Complete Example</h2>

          <div className="mockup-code">
            <pre data-prefix="1">
              <code>{'<!DOCTYPE html>'}</code>
            </pre>
            <pre data-prefix="2">
              <code>{'<html lang="en">'}</code>
            </pre>
            <pre data-prefix="3">
              <code>{'<head>'}</code>
            </pre>
            <pre data-prefix="4">
              <code>{'  <meta charset="UTF-8">'}</code>
            </pre>
            <pre data-prefix="5">
              <code>{'  <title>My Contact Form</title>'}</code>
            </pre>
            <pre data-prefix="6">
              <code>{'  <script src="https://exapps.tbit.io/embed/forms.js"></script>'}</code>
            </pre>
            <pre data-prefix="7">
              <code>{'</head>'}</code>
            </pre>
            <pre data-prefix="8">
              <code>{'<body>'}</code>
            </pre>
            <pre data-prefix="9">
              <code>{'  <h1>Contact Us</h1>'}</code>
            </pre>
            <pre data-prefix="10">
              <code>{'  '}</code>
            </pre>
            <pre data-prefix="11">
              <code>{'  <div id="tbit-form" '}</code>
            </pre>
            <pre data-prefix="12">
              <code>{'       data-root-id="507f1f77bcf86cd799439011"'}</code>
            </pre>
            <pre data-prefix="13">
              <code>{'       data-width="100%"'}</code>
            </pre>
            <pre data-prefix="14">
              <code>{'       data-height="auto"'}</code>
            </pre>
            <pre data-prefix="15">
              <code>{'       data-theme="tbitLight">'}</code>
            </pre>
            <pre data-prefix="16">
              <code>{'  </div>'}</code>
            </pre>
            <pre data-prefix="17">
              <code>{''}</code>
            </pre>
            <pre data-prefix="18">
              <code>{'  <script>'}</code>
            </pre>
            <pre data-prefix="19">
              <code>{'    document.getElementById("tbit-form")'}</code>
            </pre>
            <pre data-prefix="20">
              <code>{'      .addEventListener("tbitFormSubmitted", (e) => {'}</code>
            </pre>
            <pre data-prefix="21">
              <code>{'        alert("Thank you for your submission!");'}</code>
            </pre>
            <pre data-prefix="22">
              <code>{'      });'}</code>
            </pre>
            <pre data-prefix="23">
              <code>{'  </script>'}</code>
            </pre>
            <pre data-prefix="24">
              <code>{'</body>'}</code>
            </pre>
            <pre data-prefix="25">
              <code>{'</html>'}</code>
            </pre>
          </div>
        </section>

        {/* Support */}
        <section>
          <h2 className="text-3xl font-bold text-primary mb-4">Need Help?</h2>
          <div className="alert alert-info">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              className="stroke-current shrink-0 w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <div>
              <div className="font-bold">Questions or issues?</div>
              <div className="text-sm">
                Check out our{' '}
                <Link to="/docs/forms" className="link link-primary">
                  Forms Documentation
                </Link>{' '}
                or contact support at{' '}
                <a href="mailto:support@tbit.io" className="link link-primary">
                  support@tbit.io
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
